import { useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { Ad, AdPositionEnum } from "../client/api";
import { pickAd, isAdActive } from "../config/utils";
import { selectAreaOfInterest } from "../selectors";
import { buildAdsKey, fetchAdsByPosition } from "../store/ads/adsSlice";
import type { AppDispatch, RootState } from "../store";
import { ClavaContext } from "../config/contexts";
import { AdPositionEnum as AdPos } from "../client/api";
import { isAdSenseOnlyPosition } from "../config/adsenseConfig";

type UseAdSelectionOptions = {
  type: AdPositionEnum;
  canShowAdSense?: boolean;
  skipSelection?: boolean;
};

export function useAdSelection({
  type,
  canShowAdSense = false,
  skipSelection = false,
}: UseAdSelectionOptions) {
  const dispatch = useDispatch<AppDispatch>();
  const areaOfInterest = useSelector(selectAreaOfInterest);
  const aoiId = areaOfInterest?.id;
  const adsState = useSelector((state: RootState) => state.ads.value);
  const { pro } = useContext(ClavaContext);

  const filteredAds = useMemo(() => {
    if (!type || typeof aoiId !== "number") {
      return undefined;
    }

    const positions: AdPositionEnum[] =
      type === AdPos.HOME_MATCH 
        ? [AdPos.HOME_MATCH, AdPos.HOME_MATCH_DOUBLE]
        : [type];

    const allAds: Ad[] = [];
    let anyUndefined = false;

    positions.forEach((pos) => {
      const key = buildAdsKey(pos, aoiId);
      const ads = adsState[key];
      if (ads === undefined) {
        anyUndefined = true;
      } else {
        allAds.push(...ads);
      }
    });

    if (anyUndefined && allAds.length === 0) {
      return undefined;
    }

    return allAds.filter(isAdActive);
  }, [adsState, aoiId, type]);

  useEffect(() => {
    if (!type || pro || typeof aoiId !== "number") {
      return;
    }

    if (filteredAds !== undefined) {
      return;
    }
    const positions: AdPositionEnum[] =
      type === AdPos.HOME_MATCH 
        ? [AdPos.HOME_MATCH, AdPos.HOME_MATCH_DOUBLE]
        : [type];

    positions.forEach((pos) => {
      if (isAdSenseOnlyPosition(pos)) {
        return;
      }

      const key = buildAdsKey(pos, aoiId);
      if (adsState[key] === undefined) {
        dispatch(fetchAdsByPosition(pos, aoiId));
      }
    });
  }, [dispatch, aoiId, type, filteredAds, adsState, pro]);

  const selectedAd = useMemo(() => {
    if (skipSelection || !filteredAds || filteredAds.length === 0) {
      return null;
    }

    return pickAd(filteredAds, canShowAdSense);
  }, [filteredAds, canShowAdSense, skipSelection]);

  return {
    selectedAd,
    filteredAds,
    pro,
  };
}

